import { useState } from 'react';
import { FeatureItemConfig } from '@/lib/themeConfig';
import { SaveButton } from './GeneralSection';

interface Props {
  data: FeatureItemConfig[];
  onChange: (data: FeatureItemConfig[]) => void;
}

const ICON_OPTIONS = [
  'ri-truck-line', 'ri-leaf-line', 'ri-award-line', 'ri-refresh-line',
  'ri-shield-check-line', 'ri-gift-line', 'ri-star-line', 'ri-heart-line',
  'ri-time-line', 'ri-phone-line', 'ri-map-pin-line', 'ri-mail-line',
  'ri-lock-line', 'ri-customer-service-line', 'ri-recycle-line', 'ri-global-line',
];

export default function FeaturesSectionEditor({ data, onChange }: Props) {
  const [features, setFeatures] = useState<FeatureItemConfig[]>(data);
  const [saved, setSaved] = useState(false);

  const update = (idx: number, key: keyof FeatureItemConfig, value: string) => {
    setFeatures(prev => prev.map((f, i) => i === idx ? { ...f, [key]: value } : f));
  };

  const add = () => {
    setFeatures(prev => [...prev, { icon: 'ri-star-line', title: '', description: '' }]);
  };

  const remove = (idx: number) => {
    if (features.length <= 1) return;
    setFeatures(prev => prev.filter((_, i) => i !== idx));
  };

  const handleSave = () => {
    onChange(features);
    setSaved(true);
    setTimeout(() => setSaved(false), 2000);
  };

  return (
    <div className="max-w-3xl space-y-4">
      {features.map((f, i) => (
        <div key={i} className="bg-white rounded-2xl p-5 space-y-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 flex items-center justify-center bg-amber-100 rounded-lg">
                <i className={`${f.icon} text-amber-600`} />
              </div>
              <span className="text-sm font-medium text-stone-700">Özellik {i + 1}</span>
            </div>
            {features.length > 1 && (
              <button
                onClick={() => remove(i)}
                className="text-xs text-red-500 hover:text-red-700 transition-colors cursor-pointer whitespace-nowrap flex items-center gap-1"
              >
                <i className="ri-delete-bin-line" /> Sil
              </button>
            )}
          </div>

          <div>
            <label className="block text-xs font-medium text-stone-600 mb-1.5">İkon</label>
            <div className="flex flex-wrap gap-2">
              {ICON_OPTIONS.map(icon => (
                <button
                  key={icon}
                  onClick={() => update(i, 'icon', icon)}
                  className={`w-9 h-9 flex items-center justify-center rounded-lg transition-all cursor-pointer ${
                    f.icon === icon ? 'bg-amber-600 text-white' : 'bg-stone-100 text-stone-600 hover:bg-stone-200'
                  }`}
                  title={icon}
                >
                  <i className={icon} />
                </button>
              ))}
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-medium text-stone-600 mb-1.5">Başlık</label>
              <input
                type="text"
                value={f.title}
                onChange={e => update(i, 'title', e.target.value)}
                className="w-full px-4 py-2.5 text-sm bg-stone-50 rounded-xl border border-stone-200 focus:border-amber-500 focus:outline-none transition-all"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-stone-600 mb-1.5">Açıklama</label>
              <input
                type="text"
                value={f.description}
                onChange={e => update(i, 'description', e.target.value)}
                className="w-full px-4 py-2.5 text-sm bg-stone-50 rounded-xl border border-stone-200 focus:border-amber-500 focus:outline-none transition-all"
              />
            </div>
          </div>
        </div>
      ))}

      <button
        onClick={add}
        className="w-full py-3 border-2 border-dashed border-stone-300 rounded-2xl text-sm text-stone-500 hover:border-amber-400 hover:text-amber-600 transition-all cursor-pointer flex items-center justify-center gap-2"
      >
        <i className="ri-add-line" /> Yeni Özellik Ekle
      </button>

      <SaveButton saved={saved} onSave={handleSave} />
    </div>
  );
}
